import React, { useState, useEffect } from 'react';
import IntegrationCard from '../IntegrationCard/IntegrationCard';
import { motion } from 'framer-motion';
import WhatsappConnectModal from '../WhatsappConnectModal/WhatsappConnectModal';
import SerpApiConfigModal from '../SerpApiConfigModal/SerpApiConfigModal';
import WhatsappDisconnectModal from '../WhatsappDisconnectModal/WhatsappDisconnectModal'; // Novo import
import { saveCredential, deleteCredential, fetchCredential } from '../../../../services/credentialsService';
import { checkInstanceStatus, disconnectInstance } from '../../../../services/whatsappService'; // Importando disconnectInstance
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const IntegrationList: React.FC = () => {
  const { t } = useTranslation();

  // WhatsApp State
  const [isWhatsappModalOpen, setIsWhatsappModalOpen] = useState(false);
  const [isWhatsappDisconnectModalOpen, setIsWhatsappDisconnectModalOpen] = useState(false); // Novo estado
  const [whatsappInstanceName, setWhatsappInstanceName] = useState<string | null>(null);
  const [whatsappUniqueName, setWhatsappUniqueName] = useState<string | null>(null);
  const [isWhatsappConnected, setIsWhatsappConnected] = useState(false);
  const [isLoadingWhatsapp, setIsLoadingWhatsapp] = useState(true);

  // SerpApi State
  const [isSerpApiModalOpen, setIsSerpApiModalOpen] = useState(false);
  const [serpApiKey, setSerpApiKey] = useState('');
  const [isSerpApiConnected, setIsSerpApiConnected] = useState(false);
  const [isLoadingSerpApi, setIsLoadingSerpApi] = useState(true);

  // Função para carregar o status do WhatsApp
  const loadWhatsappStatus = async () => {
    try {
      const whatsappCreds = await fetchCredential('whatsapp');
      if (whatsappCreds) {
        const instanceToken = whatsappCreds.api_key; // Token UAZAPI da instância
        const baseName = whatsappCreds.instance_details?.name;

        if (instanceToken && baseName) {
          setWhatsappUniqueName(instanceToken); // Guarda o token como identificador
          setWhatsappInstanceName(baseName);

          // Verificar status em tempo real usando o token
          const statusData = await checkInstanceStatus(instanceToken);

          if (statusData.status === 'CONNECTED') {
            setIsWhatsappConnected(true);
          } else {
            setIsWhatsappConnected(false);
          }
        } else {
          setIsWhatsappConnected(false);
        }
      }
    } catch (error) {
      console.error('WhatsApp status check failed:', error);
      setIsWhatsappConnected(false);
    } finally {
      setIsLoadingWhatsapp(false);
    }
  };

  // Carregar status inicial de ambas as integrações
  useEffect(() => {
    const loadStatuses = async () => {
      // Carregar SerpApi
      try {
        const serpCreds = await fetchCredential('serpapi');
        if (serpCreds && serpCreds.api_key) {
          setSerpApiKey(serpCreds.api_key);
          setIsSerpApiConnected(true);
        }
      } catch (error) {
        toast.error(t('Falha ao carregar status da SerpApi.'));
      } finally {
        setIsLoadingSerpApi(false);
      }

      await loadWhatsappStatus();
    };
    loadStatuses();
  }, [t]);

  // WhatsApp Handlers (Conectado ao Supabase/n8n)
  const handleConnectWhatsapp = (instanceName: string, instanceToken: string) => {
    setWhatsappInstanceName(instanceName);
    setWhatsappUniqueName(instanceToken); // Guarda o token UAZAPI como identificador
    setIsWhatsappConnected(true);
    setIsWhatsappModalOpen(false);
  };

  const handleDisconnectWhatsapp = async () => {
    if (!whatsappUniqueName) {
      toast.error(t('Nenhuma instância para desconectar.'));
      setIsWhatsappDisconnectModalOpen(false);
      return;
    }

    const toastId = toast.loading(t('Desconectando instância do WhatsApp...'));

    try {
      // 1. Chamar a UAZAPI para desconectar usando o token da instância (se possível)
      try {
        await disconnectInstance(whatsappUniqueName);
      } catch (remoteError) {
        console.warn('Falha ao remover instância remota na UAZAPI (pode já estar excluída ou sem permissão):', remoteError);
      }

      // 2. Deletar as credenciais no Supabase
      await deleteCredential('whatsapp');

      // 3. Resetar o estado local
      setWhatsappInstanceName(null);
      setWhatsappUniqueName(null);
      setIsWhatsappConnected(false);
      setIsWhatsappDisconnectModalOpen(false);

      toast.success(t('WhatsApp desconectado e credencial removida com sucesso!'), { id: toastId });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('Erro ao desconectar o WhatsApp.');
      toast.error(errorMessage, { id: toastId });
    }
  };

  const handleViewWhatsappStatus = () => {
    if (whatsappUniqueName) {
      const toastId = toast.loading(t('Verificando status em tempo real...'));
      // whatsappUniqueName agora guarda o token UAZAPI
      checkInstanceStatus(whatsappUniqueName)
        .then(statusData => {
          toast.dismiss(toastId);
          if (statusData.status === 'CONNECTED') {
            toast.success(t('Instância {{instanceName}} está CONECTADA e pronta para uso.', {
              instanceName: whatsappInstanceName || 'N/A',
            }));
            setIsWhatsappConnected(true);
          } else if (statusData.status === 'QRCODE') {
            toast(t('Instância {{instanceName}} está AGUARDANDO QR CODE. Abra o modal para escanear.', {
              instanceName: whatsappInstanceName || 'N/A',
            }), { icon: '⚠️' });
            setIsWhatsappConnected(false);
          } else {
            toast.error(t('Instância {{instanceName}} está DESCONECTADA ou com erro. Por favor, reconecte.', {
              instanceName: whatsappInstanceName || 'N/A',
            }));
            setIsWhatsappConnected(false);
          }
        })
        .catch(() => {
          toast.error(t('Falha ao verificar o status em tempo real.'), { id: toastId });
          setIsWhatsappConnected(false);
        });
    } else {
      toast.error(t('Nenhuma instância configurada.'));
    }
  };

  // SerpApi Handlers (Conectado ao Supabase)
  const handleSaveSerpApi = async (key: string) => {
    try {
      await saveCredential('serpapi', { api_key: key, is_active: true });
      setSerpApiKey(key);
      setIsSerpApiConnected(true);
      toast.success(t('Chave SerpApi salva e integração ativada!'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('Erro ao salvar a chave SerpApi.');
      toast.error(errorMessage);
    }
  };

  const handleDisconnectSerpApi = async () => {
    try {
      await deleteCredential('serpapi');
      setSerpApiKey('');
      setIsSerpApiConnected(false);
      toast.success(t('SerpApi desconectada com sucesso.'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('Erro ao desconectar a SerpApi.');
      toast.error(errorMessage);
    }
  };

  const handleViewSerpApiStatus = () => {
    alert(t('SerpApi Status: {{status}}. Chave: {{key}}...', {
      status: isSerpApiConnected ? t('integrations.status_connected') : t('integrations.status_disconnected'),
      key: serpApiKey.substring(0, 5)
    }));
  };

  if (isLoadingSerpApi || isLoadingWhatsapp) {
    return (
      <div className="flex items-center justify-center h-40">
        <span className="material-symbols-outlined text-4xl text-primary animate-spin">sync</span>
        <p className="ml-4 text-lg text-gray-600 dark:text-gray-400">{t('integrations.loading')}</p>
      </div>
    );
  }

  const integrationsData = [
    {
      id: 1,
      title: t('integrations.whatsapp_title'),
      icon: <span className="material-symbols-outlined text-4xl">chat</span>,
      iconBgColor: 'bg-green-100 dark:bg-green-900/50',
      iconTextColor: 'text-green-600 dark:text-green-300',
      buttonText: t('integrations.whatsapp_button_connect'),
      isComingSoon: false,
      isConnected: isWhatsappConnected,
      onAction: () => setIsWhatsappModalOpen(true),
      onDisconnect: () => setIsWhatsappDisconnectModalOpen(true),
      onViewStatus: handleViewWhatsappStatus,
      dataTour: 'whatsapp-integration', // Added for tour
    },
    {
      id: 2,
      title: t('integrations.serpapi_title'),
      icon: <span className="material-symbols-outlined text-4xl">api</span>,
      iconBgColor: 'bg-blue-100 dark:bg-blue-900/50',
      iconTextColor: 'text-blue-600 dark:text-blue-300',
      buttonText: isSerpApiConnected ? t('integrations.serpapi_button_configure') : t('integrations.whatsapp_button_connect'),
      isComingSoon: false,
      isConnected: isSerpApiConnected,
      onAction: () => setIsSerpApiModalOpen(true),
      onDisconnect: handleDisconnectSerpApi,
      onViewStatus: handleViewSerpApiStatus,
      dataTour: 'serpapi-integration', // Added for tour
    },
    {
      id: 3,
      title: t('integrations.email_title'),
      icon: <span className="material-symbols-outlined text-4xl">mail</span>,
      iconBgColor: 'bg-gray-100 dark:bg-gray-700',
      iconTextColor: 'text-gray-500 dark:text-gray-400',
      buttonText: t('integrations.email_button_configure'),
      isComingSoon: true,
      onAction: () => { },
    },
  ];

  return (
    <>
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        {integrationsData.map((integration, index) => (
          <motion.div
            key={integration.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 + index * 0.1, type: "spring", stiffness: 300, damping: 20 }}
          >
            <IntegrationCard
              title={integration.title}
              icon={integration.icon}
              iconBgColor={integration.iconBgColor}
              iconTextColor={integration.iconTextColor}
              buttonText={integration.buttonText}
              onAction={integration.onAction}
              isComingSoon={integration.isComingSoon}
              isConnected={integration.isConnected}
              onDisconnect={integration.onDisconnect}
              onViewStatus={integration.onViewStatus}
              dataTour={integration.dataTour} // Pass to card
            />
          </motion.div>
        ))}
      </motion.div>

      <WhatsappConnectModal
        isOpen={isWhatsappModalOpen}
        onClose={() => setIsWhatsappModalOpen(false)}
        onConnect={handleConnectWhatsapp}
      />

      <SerpApiConfigModal
        isOpen={isSerpApiModalOpen}
        onClose={() => setIsSerpApiModalOpen(false)}
        onSave={handleSaveSerpApi}
        initialApiKey={serpApiKey}
      />

      {/* Novo Modal de Confirmação de Desconexão */}
      <WhatsappDisconnectModal
        isOpen={isWhatsappDisconnectModalOpen}
        onClose={() => setIsWhatsappDisconnectModalOpen(false)}
        onConfirm={handleDisconnectWhatsapp}
        instanceName={whatsappInstanceName || t('Instância')}
      />
    </>
  );
};

export default IntegrationList;